import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { addYears } from 'date-fns/addYears'
import { differenceInDays } from 'date-fns/differenceInDays'
import { differenceInMonths } from 'date-fns/differenceInMonths'
import { differenceInWeeks } from 'date-fns/differenceInWeeks'
import { format } from 'date-fns/format'
import { startOfToday } from 'date-fns/startOfToday'

import type { PieValueType } from '@mui/x-charts'

import { Check } from '@mxenabled/mx-icons'

import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { type MonthlySummaries } from '../../core'
import type {
  CreditScoreStatusCopy,
  FinstrongCopy,
  KeyIndicatorSpend,
} from '../../core/types/localization/FinstrongCopy'
import { CardStatus, FinstrongThresholds } from '../../common/constants/FinStrong'

import { Action } from '../components/ProfileList'
import { formatCurrency } from '../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../core/utils/Localization'

export const radiansToDegrees = (radians: number): number => {
  return radians * (180 / Math.PI)
}

export const calculateFinstrongScoreStatus = (healthScore: number, copy: FinstrongCopy) => {
  const scoreRanges = [
    {
      description: copy.vulnerable_description,
      max: 25,
      scoreContent: copy.accordion_details_text.vulnerable,
      status: copy.vulnerable,
    },
    {
      description: copy.building_description,
      max: 50,
      scoreContent: copy.accordion_details_text.building,
      status: copy.building,
    },
    {
      description: copy.stable_description,
      max: 75,
      scoreContent: copy.accordion_details_text.stable,
      status: copy.stable,
    },
    {
      description: copy.strong_description,
      max: 100,
      scoreContent: copy.accordion_details_text.strong,
      status: copy.strong,
    },
  ]

  const foundRange = scoreRanges.find((range) => healthScore > 0 && healthScore <= range.max)

  return foundRange || { status: copy.insufficient_data, description: '', scoreContent: '' }
}

export const CreditScoreLightColors = {
  POOR: '#8C0E0E',
  FAIR: '#E32727',
  GOOD: '#FFB70D',
  VERY_GOOD: '#0EA26C',
  EXCEPTIONAL: '#005C3D',
}

export const CreditScoreDarkColors = {
  POOR: '#B21C1C',
  FAIR: '#FF3232',
  GOOD: '#FFC02C',
  VERY_GOOD: '#29C76B',
  EXCEPTIONAL: '#008A5A',
}

export const getCreditScoreDetails = (
  healthScore: number,
  isDarkMode: boolean,
  copy: CreditScoreStatusCopy,
) => {
  if (healthScore >= 300 && healthScore < 580) {
    return {
      status: copy.poor,
      color: isDarkMode ? CreditScoreDarkColors.POOR : CreditScoreLightColors.POOR,
    }
  } else if (healthScore >= 580 && healthScore < 670) {
    return {
      status: copy.fair,
      color: isDarkMode ? CreditScoreDarkColors.FAIR : CreditScoreLightColors.FAIR,
    }
  } else if (healthScore >= 670 && healthScore < 740) {
    return {
      status: copy.good,
      color: isDarkMode ? CreditScoreDarkColors.GOOD : CreditScoreLightColors.GOOD,
    }
  } else if (healthScore >= 740 && healthScore < 800) {
    return {
      status: copy.very_good,
      color: isDarkMode ? CreditScoreDarkColors.VERY_GOOD : CreditScoreLightColors.VERY_GOOD,
    }
  } else if (healthScore >= 800 && healthScore <= 850) {
    return {
      status: copy.exceptional,
      color: isDarkMode ? CreditScoreDarkColors.EXCEPTIONAL : CreditScoreLightColors.EXCEPTIONAL,
    }
  }
  return { status: copy.missing_data }
}

export const getOutterArcStartEndAngle = (
  healthScoreData: PieValueType[],
  totalAngularSpan: number,
  arcStartAngle: number,
  dataIndex: number,
) => {
  const total = healthScoreData.reduce((sum, item) => sum + item.value, 0)

  let cumulativeAngle = 0

  const arcs = healthScoreData.map((item) => {
    const anglePortion = (item.value / total) * totalAngularSpan

    const sliceStart = arcStartAngle + cumulativeAngle
    const sliceEnd = sliceStart + anglePortion

    cumulativeAngle += anglePortion

    return {
      ...item,
      startAngle: sliceStart,
      endAngle: sliceEnd,
    }
  })
  return [arcs[dataIndex].startAngle, arcs[dataIndex].endAngle]
}

export const getFilledArcStartEndAngle = (
  outterArcStartAngle: number,
  outterArcEndAngle: number,
  dataIndex: number,
  unitsPerSegment: number,
  progressEndAngle: number,
) => {
  let filledArcStartAngle = 0
  let filledArcEndAngle = 0
  const wholeSegments = Math.floor(unitsPerSegment)
  const partialSegment = unitsPerSegment - wholeSegments
  if (wholeSegments > dataIndex) {
    filledArcStartAngle = outterArcStartAngle
    filledArcEndAngle = outterArcEndAngle
  } else if (wholeSegments === dataIndex && partialSegment > 0) {
    filledArcStartAngle = outterArcStartAngle
    filledArcEndAngle = progressEndAngle
  }
  return [filledArcStartAngle, filledArcEndAngle]
}

export const getMarkerXYCoordinates = (
  markerRadius: number,
  progressEndAngle: number,
  angleOffset: number = 0,
) => {
  const markerX = markerRadius * Math.sin(progressEndAngle - angleOffset)
  const markerY = -markerRadius * Math.cos(progressEndAngle - angleOffset)
  return [markerX, markerY]
}

export const buildProfileItem = ({
  dynamicCopy,
  fallbackLabel,
  icon,
  key,
  label,
  value,
}: {
  dynamicCopy?: string
  fallbackLabel: string
  icon: React.JSX.Element
  key: Action
  label: string
  value: string | number | undefined | null
}) => {
  const isComplete = Boolean(value)

  const secondaryText = isComplete
    ? getFormattedValue(key, value!)
    : (dynamicCopy && (buildDynamicCopy(dynamicCopy, fallbackLabel) as string)) || fallbackLabel

  return {
    icon: isComplete ? <Check /> : icon,
    isComplete,
    key,
    primaryText: label,
    secondaryText,
  }
}

const getFormattedValue = (key: Action, value: string | number) => {
  const formatters: Partial<Record<Action, (value: string | number) => string>> = {
    [Action.DateOfBirth]: (v) => format(new Date(Number(v) * 1000), 'MMM d, yyyy'),
    [Action.MonthlyIncome]: (v) => formatCurrency(Number(v), '0.00'),
  }

  return formatters[key]?.(value) ?? String(value)
}

export const getPercentFromRatio = (ratio: number | null) => {
  return ratio !== null ? Math.round(ratio * 100) : null
}

export const getMonthName = (month: number): string => {
  const date = new Date(new Date().getFullYear(), month - 1, 1)
  return formatDate(date, DATE_FORMATS_INTL.MONTH_SHORT)
}

export const getDaysOfCashOnHandState = (days: number | null): keyof typeof CardStatus => {
  const { good, poor } = FinstrongThresholds.daysOfCashOnHand

  if (days === null) return 'MISSING'
  if (days >= good) return 'GOOD'
  if (days >= poor) return 'FAIR'

  return 'OFF_TRACK'
}

export const getEmergencySavingsState = (dollars: number | null): keyof typeof CardStatus => {
  const { good, poor } = FinstrongThresholds.emergencySavings

  if (dollars === null) return 'MISSING'
  if (dollars >= good) return 'GOOD'
  if (dollars >= poor) return 'FAIR'
  return 'OFF_TRACK'
}

export const getSpendToIncomeRatioState = (ratio: number | null): keyof typeof CardStatus => {
  const { good, poor } = FinstrongThresholds.spendToIncomeRatio

  if (ratio === null) return 'MISSING'
  if (ratio < good) return 'GOOD'
  if (ratio < poor) return 'FAIR'
  return 'OFF_TRACK'
}

export const getManageDebtRatioState = (ratio: number | null): keyof typeof CardStatus => {
  const { good, poor } = FinstrongThresholds.debtToIncomeRatio

  if (ratio === null) return 'MISSING'
  if (ratio <= good) return 'GOOD'
  if (ratio <= poor) return 'FAIR'
  return 'OFF_TRACK'
}

export const getCreditScoreState = (score: number | null): keyof typeof CardStatus => {
  const { good, poor } = FinstrongThresholds.creditScore

  if (score === null) return 'MISSING'
  if (score >= good) return 'GOOD'
  if (score >= poor) return 'FAIR'
  return 'OFF_TRACK'
}

export const getSpendingFeeCountState = (count: number | null) => {
  if (count === null) return 'MISSING'
  if (count === 0) return 'GOOD'
  return 'OFF_TRACK'
}

export const getSpendingFeeCountStatus = (
  count: number | null,
  spendCopy: KeyIndicatorSpend,
): {
  cardState: keyof typeof CardStatus
  description: string
  title: string
} => {
  const state = getSpendingFeeCountState(count)

  const cardStatusMapping = {
    GOOD: spendCopy.count_good_description,
    OFF_TRACK: spendCopy.count_off_track_description,
    MISSING: spendCopy.count_missing_description,
  }

  return {
    cardState: state,
    description: cardStatusMapping[state],
    title: spendCopy.count_card_title,
  }
}

export const getCardStateStyles = (cardState: string) => {
  return {
    [CardStatus.GOOD]: { backgroundColor: 'success.lighter', color: 'success.dark' },
    [CardStatus.FAIR]: { backgroundColor: 'warning.lighter', color: 'warning.dark' },
    [CardStatus.OFF_TRACK]: { backgroundColor: 'error.lighter', color: 'error.dark' },
    [CardStatus.MISSING]: { backgroundColor: 'divider', color: 'text.secondary' },
  }[cardState]
}

const DEFAULT_CONTRIBUTION_PERIOD_INDEX = 1
const DEFAULT_TARGET_AMOUNT = 1

const getFutureDate = {
  1: addMonths,
  2: addYears,
  3: addDays,
} as const

const getNumberOfPayments = {
  1: differenceInMonths,
  2: (end: Date, start: Date) => differenceInWeeks(end, start) / 2,
  3: differenceInWeeks,
  4: differenceInDays,
} as const

const SAVINGS_BUCKETS = [
  { contributionPeriodIndex: 3, targetAmount: 6, upperLimit: 500 },
  { contributionPeriodIndex: 3, targetAmount: 3, upperLimit: 900 },
  { contributionPeriodIndex: 2, targetAmount: 1, upperLimit: 1000 },
]

interface CalculateContributionParams {
  contribution: keyof typeof getNumberOfPayments
  goalAmount: number
  goalPeriod: number
  targetPeriod: keyof typeof getFutureDate
  totalSaved: number
}

export const calculateContribution = ({
  contribution,
  goalAmount,
  goalPeriod,
  targetPeriod,
  totalSaved,
}: CalculateContributionParams) => {
  const amountRemaining = goalAmount - totalSaved

  if (amountRemaining <= 0) return 0

  const today = startOfToday()
  const futureDateFn = getFutureDate[targetPeriod]
  const paymentFn = getNumberOfPayments[contribution]

  if (!futureDateFn || !paymentFn) return amountRemaining

  const futureDate = futureDateFn(today, goalPeriod)
  const payments = paymentFn(futureDate, today)

  return Math.max(amountRemaining / (payments || 1), 1)
}

export const calculatePercentage = (amount: number | null): string => {
  if (!amount || amount <= 0) return '0%'
  if (amount >= 1000) return '100%'

  return `${Math.round(amount / 10)}%`
}

export const getInitialTargetPeriodAmount = (totalSaved: number) => {
  const bucket = SAVINGS_BUCKETS.find((bucket) => totalSaved < bucket.upperLimit)

  return bucket?.targetAmount ?? DEFAULT_TARGET_AMOUNT
}

export const getInitialContributionPeriod = (totalSaved: number) => {
  const bucket = SAVINGS_BUCKETS.find((bucket) => totalSaved < bucket.upperLimit)
  const periodIndex = bucket?.contributionPeriodIndex ?? DEFAULT_CONTRIBUTION_PERIOD_INDEX

  return periodIndex
}

export const getSeriesGraphData = (
  monthlySummaries: MonthlySummaries[],
  valueKey: keyof Pick<MonthlySummaries, 'available_cash' | 'debt_balance' | 'credit_score'>,
) => {
  const dataSet = [...monthlySummaries]
    .sort((a, b) => {
      return a.year === b.year ? a.month - b.month : a.year - b.year
    })
    .map((summary) => ({
      x: formatDate(new Date(0, summary.month - 1, 1), DATE_FORMATS_INTL.MONTH_SHORT),
      y: summary[valueKey],
    }))

  return dataSet
}
